import { KeyboardEvent } from "react";

import { resolveTwoLetterCode } from "../../helpers/language";
import { PlayerStoreProperties } from "../../store/store";
import {
  ChromecastServiceScheduleSubtitles,
  Subtitles
} from "../../types/player-api";

export const stopEventPropagation = e => e.stopPropagation();

export const handleKeyboardEvent = (e: KeyboardEvent) => {
  if (e.key === " ") {
    e.stopPropagation();
  }
};

export const resolveBuffered = ({
  buffered,
  currentTime
}: {
  buffered?: TimeRanges;
  currentTime: number;
}) => {
  if (!buffered) {
    return 0;
  }

  let closestBuffer = 0;

  for (let i = 0; i < buffered.length; i++) {
    const bufferedEnd = buffered.end(i);
    if (
      bufferedEnd > currentTime &&
      (closestBuffer === 0 ||
        bufferedEnd - currentTime < closestBuffer - currentTime)
    ) {
      closestBuffer = buffered.end(i);
    }
  }
  return closestBuffer;
};

const onCueChange = ({
  setSubtitlesCue,
  textTrack
}: {
  setSubtitlesCue: PlayerStoreProperties["setSubtitlesCue"];
  textTrack: TextTrack;
}) => {
  if (textTrack.activeCues && textTrack.activeCues.length > 0) {
    setSubtitlesCue(
      (Array.from(textTrack.activeCues) as VTTCue[])
        .reduce<string[]>((cueTexts, cue) => {
          const trimmed = cue.text.trim();
          cue.onexit = () => setSubtitlesCue("");
          if (!cueTexts.includes(trimmed)) {
            cueTexts.push(trimmed);
          }

          return cueTexts;
        }, [])
        .join("\n")
    );
  }
};

export const resolveTextTrack = ({
  setSubtitles,
  setSubtitlesCue,
  textTrack,
  usingServiceScheduleTracks = false
}: {
  setSubtitles: PlayerStoreProperties["setSubtitles"];
  setSubtitlesCue: PlayerStoreProperties["setSubtitlesCue"];
  textTrack: TextTrack;
  usingServiceScheduleTracks?: boolean;
}) => {
  if (
    (textTrack.kind === "subtitles" || textTrack.kind === "captions") &&
    textTrack.language
  ) {
    if (!textTrack.oncuechange) {
      textTrack.oncuechange = () => {
        onCueChange({ setSubtitlesCue, textTrack });
      };
    }
    if (!usingServiceScheduleTracks) {
      const newSubtitlesItem = parseSubtitlesItem(textTrack);

      setSubtitles(prevSubtitles =>
        prevSubtitles.some(e => e && e.label === newSubtitlesItem.label)
          ? prevSubtitles
          : [...prevSubtitles, newSubtitlesItem]
      );
    }
  }
};

export const resolveSelectedAudioTrack = (
  audioTracks,
  language,
  setSelectedAudioTrackId,
  userPreferredAudioTrack
) => {
  if (audioTracks.length) {
    const matchingUserPreferredAudioTrack =
      userPreferredAudioTrack &&
      audioTracks.find(
        track =>
          resolveTwoLetterCode(track.language) ===
            userPreferredAudioTrack.language &&
          (track.type === userPreferredAudioTrack.type ||
            (track.type === "AudioTrack" && !userPreferredAudioTrack.type))
      );

    if (matchingUserPreferredAudioTrack) {
      setSelectedAudioTrackId(matchingUserPreferredAudioTrack.id);
    } else {
      const defaultAudioTrackByUILanguage = audioTracks.find(
        track =>
          resolveTwoLetterCode(track.language) ===
            resolveTwoLetterCode(language) &&
          (!track.type || track.type === "AudioTrack")
      );

      setSelectedAudioTrackId(
        defaultAudioTrackByUILanguage
          ? defaultAudioTrackByUILanguage.id
          : audioTracks[0].id
      );
    }
  } else {
    setSelectedAudioTrackId(-1);
  }
};

export const parseSubtitlesItem = (
  textTrack: TextTrack | Subtitles
): Subtitles => ({
  label: textTrack.label?.toLocaleLowerCase(),
  language: resolveTwoLetterCode(textTrack.language)
});

export const parseChromecastServiceScheduleSubtitlesItem = (
  textTrack: ChromecastServiceScheduleSubtitles
): Subtitles => ({
  label: textTrack.trackKey?.toLocaleLowerCase(),
  language: resolveTwoLetterCode(textTrack.language)
});

export const parseChromecastSubtitlesItem = (
  textTrack: chrome.cast.media.Track
): Subtitles => ({
  label: textTrack.name?.toLocaleLowerCase(),
  language: resolveTwoLetterCode(textTrack.language)
});
